import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Upload,
  X,
  FileText,
  LogOut,
  Loader2,
  Check,
} from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import api from '../lib/api';
import { removeToken } from '../lib/auth';

const ONBOARDING_STEPS = [
  { number: 1, title: 'Personal Details', description: 'Address & Emergency Contact' },
  { number: 2, title: 'Document Verification', description: 'Upload Required Documents' },
  { number: 3, title: 'Banking Information', description: 'Salary Account Details' },
  { number: 4, title: 'Policy Acknowledgment', description: 'Review & Accept' },
  { number: 5, title: 'Profile Setup', description: 'Complete Your Profile' },
];

export default function OnboardingPage() {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [onboarding, setOnboarding] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [stepData, setStepData] = useState({});
  const [uploadedDocs, setUploadedDocs] = useState({});
  const [uploadingDoc, setUploadingDoc] = useState(null);

  useEffect(() => {
    const loadOnboarding = async () => {
      try {
        const response = await api.get(`/api/onboarding/${applicationId}`);
        setOnboarding(response.data);
        setCurrentStep(response.data.current_step);
        setStepData(response.data.steps_data || {});
      } catch (error) {
        toast.error('Failed to load onboarding data');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    loadOnboarding();
  }, [applicationId]);

  const handleDocumentUpload = async (file, docType) => {
    setUploadingDoc(docType);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/api/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadedDocs((prev) => ({
        ...prev,
        [docType]: {
          id: response.data._id,
          filename: response.data.filename,
        },
      }));
      toast.success(`${docType} uploaded successfully!`);
    } catch (error) {
      toast.error(`Failed to upload ${docType}`);
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleSubmitStep = async () => {
    setSubmitting(true);

    try {
      let dataToSubmit = {};

      if (currentStep === 1) {
        // Personal Details
        dataToSubmit = stepData.step1 || {};
      } else if (currentStep === 2) {
        // Documents
        dataToSubmit = {
          ...stepData.step2,
          documents: uploadedDocs,
        };
      } else if (currentStep === 3) {
        // Banking
        dataToSubmit = stepData.step3 || {};
      } else if (currentStep === 4) {
        // Policies
        dataToSubmit = stepData.step4 || {};
      } else if (currentStep === 5) {
        // Profile
        dataToSubmit = {
          ...stepData.step5,
          profile_photo: uploadedDocs.profile_photo || null,
        };
      }

      const response = await api.put(
        `/api/onboarding/${applicationId}/step/${currentStep}`,
        {
          step_data: dataToSubmit,
        }
      );

      toast.success(`Step ${currentStep} completed!`);

      if (currentStep === 5) {
        toast.success('Onboarding completed! Welcome to the team! 🎉', {
          duration: 5000,
        });
        navigate(`/applications/${applicationId}`);
      } else {
        setOnboarding(response.data);
        setCurrentStep(response.data.current_step);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit step');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    removeToken();
    toast.success('Logged out successfully');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white/40 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-teal-500 animate-spin" />
      </div>
    );
  }

  const StepIndicator = () => (
    <div className="flex justify-between items-center mb-8" data-testid="onboarding-step-indicator">
      {ONBOARDING_STEPS.map((step, index) => (
        <React.Fragment key={step.number}>
          <div className="flex flex-col items-center flex-1">
            <div
              className={`w-12 h-12 rounded-full border-2 flex items-center justify-center font-semibold transition-all ${
                step.number < currentStep
                  ? 'border-emerald-500 bg-emerald-500 text-white'
                  : step.number === currentStep
                  ? 'border-teal-500 bg-teal-500 text-white'
                  : 'border-white/40 bg-white text-neutral-400'
              }`}
            >
              {step.number < currentStep ? (
                <Check className="w-6 h-6" />
              ) : (
                step.number
              )}
            </div>
            <p className="text-xs font-medium text-navy-800 mt-2 text-center hidden sm:block">
              {step.title}
            </p>
          </div>
          {index < ONBOARDING_STEPS.length - 1 && (
            <div
              className={`h-0.5 flex-1 mx-2 ${
                step.number < currentStep ? 'bg-emerald-500' : 'bg-slate-300'
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const DocumentUploadField = ({ label, docType, required = false }) => {
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      accept: {
        'application/pdf': ['.pdf'],
        'image/png': ['.png'],
        'image/jpeg': ['.jpg', '.jpeg'],
      },
      maxFiles: 1,
      maxSize: 10485760,
      onDrop: async (acceptedFiles) => {
        if (acceptedFiles.length > 0) {
          await handleDocumentUpload(acceptedFiles[0], docType);
        }
      },
    });

    const isUploaded = uploadedDocs[docType];
    const isUploading = uploadingDoc === docType;

    return (
      <div>
        <label className="block text-sm font-medium text-navy-700 mb-2">
          {label} {required && '*'}
        </label>
        {!isUploaded ? (
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
              isDragActive
                ? 'border-teal-500 bg-teal-50'
                : 'border-white/40 hover:border-teal-500'
            }`}
            data-testid={`${docType}-upload-zone`}
          >
            <input {...getInputProps()} />
            {isUploading ? (
              <div className="flex flex-col items-center">
                <Loader2 className="w-8 h-8 text-teal-500 animate-spin mb-2" />
                <p className="text-sm text-neutral-600">Uploading...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <Upload className="w-8 h-8 text-neutral-400 mb-2" />
                <p className="text-sm text-neutral-600">
                  {isDragActive ? 'Drop here' : 'Click or drag to upload'}
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl border border-emerald-200">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-emerald-600" />
              <div>
                <p className="font-medium text-navy-800">{isUploaded.filename}</p>
                <p className="text-sm text-emerald-600">Uploaded successfully</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                const newDocs = { ...uploadedDocs };
                delete newDocs[docType];
                setUploadedDocs(newDocs);
              }}
              className="p-2 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-2">
                Full Address *
              </label>
              <textarea
                required
                rows={3}
                value={stepData.step1?.address || ''}
                onChange={(e) =>
                  setStepData({
                    ...stepData,
                    step1: { ...stepData.step1, address: e.target.value },
                  })
                }
                className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors resize-none"
                placeholder="Street, City, State, Zip Code"
                data-testid="address-textarea"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-2">
                  Date of Birth *
                </label>
                <input
                  type="date"
                  required
                  value={stepData.step1?.dob || ''}
                  onChange={(e) =>
                    setStepData({
                      ...stepData,
                      step1: { ...stepData.step1, dob: e.target.value },
                    })
                  }
                  className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                  data-testid="dob-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-navy-700 mb-2">
                  Emergency Contact Name *
                </label>
                <input
                  type="text"
                  required
                  value={stepData.step1?.emergency_contact_name || ''}
                  onChange={(e) =>
                    setStepData({
                      ...stepData,
                      step1: {
                        ...stepData.step1,
                        emergency_contact_name: e.target.value,
                      },
                    })
                  }
                  className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                  placeholder="Full Name"
                  data-testid="emergency-contact-name-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-2">
                  Emergency Contact Phone *
                </label>
                <input
                  type="tel"
                  required
                  value={stepData.step1?.emergency_contact_phone || ''}
                  onChange={(e) =>
                    setStepData({
                      ...stepData,
                      step1: {
                        ...stepData.step1,
                        emergency_contact_phone: e.target.value,
                      },
                    })
                  }
                  className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                  placeholder="+1234567890"
                  data-testid="emergency-contact-phone-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-navy-700 mb-2">
                  Relationship *
                </label>
                <input
                  type="text"
                  required
                  value={stepData.step1?.emergency_contact_relationship || ''}
                  onChange={(e) =>
                    setStepData({
                      ...stepData,
                      step1: {
                        ...stepData.step1,
                        emergency_contact_relationship: e.target.value,
                      },
                    })
                  }
                  className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                  placeholder="e.g., Spouse, Parent, Sibling"
                  data-testid="emergency-contact-relationship-input"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <DocumentUploadField
              label="ID Proof (Passport, Driver's License, etc.)"
              docType="id_proof"
              required
            />
            <DocumentUploadField
              label="Address Proof (Utility Bill, Bank Statement, etc.)"
              docType="address_proof"
              required
            />
            <DocumentUploadField
              label="Educational Certificates"
              docType="education_certificates"
              required
            />
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-2">
                Bank Name *
              </label>
              <input
                type="text"
                required
                value={stepData.step3?.bank_name || ''}
                onChange={(e) =>
                  setStepData({
                    ...stepData,
                    step3: { ...stepData.step3, bank_name: e.target.value },
                  })
                }
                className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                placeholder="Bank of America"
                data-testid="bank-name-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-2">
                  Account Number *
                </label>
                <input
                  type="text"
                  required
                  value={stepData.step3?.account_number || ''}
                  onChange={(e) =>
                    setStepData({
                      ...stepData,
                      step3: {
                        ...stepData.step3,
                        account_number: e.target.value,
                      },
                    })
                  }
                  className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                  placeholder="1234567890"
                  data-testid="account-number-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-navy-700 mb-2">
                  Routing Number / IFSC Code *
                </label>
                <input
                  type="text"
                  required
                  value={stepData.step3?.routing_number || ''}
                  onChange={(e) =>
                    setStepData({
                      ...stepData,
                      step3: {
                        ...stepData.step3,
                        routing_number: e.target.value,
                      },
                    })
                  }
                  className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                  placeholder="123456789"
                  data-testid="routing-number-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-navy-700 mb-2">
                Account Holder Name *
              </label>
              <input
                type="text"
                required
                value={stepData.step3?.account_holder_name || ''}
                onChange={(e) =>
                  setStepData({
                    ...stepData,
                    step3: {
                      ...stepData.step3,
                      account_holder_name: e.target.value,
                    },
                  })
                }
                className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                placeholder="As per bank records"
                data-testid="account-holder-name-input"
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="bg-navy-50 border border-navy-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-navy-800 mb-4">
                Policies & Agreements
              </h3>
              <p className="text-neutral-700 mb-4">
                Please review and acknowledge the following policies:
              </p>
              <ul className="space-y-3 text-neutral-700 list-disc list-inside">
                <li>Code of Conduct</li>
                <li>Non-Disclosure Agreement (NDA)</li>
                <li>Employment Agreement</li>
                <li>Data Privacy Policy</li>
              </ul>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="code_of_conduct"
                  checked={stepData.step4?.code_of_conduct || false}
                  onChange={(e) =>
                    setStepData({
                      ...stepData,
                      step4: {
                        ...stepData.step4,
                        code_of_conduct: e.target.checked,
                      },
                    })
                  }
                  className="w-5 h-5 mt-1 text-teal-600 border-white/40 rounded focus:ring-2 focus:ring-teal-500"
                  data-testid="code-of-conduct-checkbox"
                />
                <label
                  htmlFor="code_of_conduct"
                  className="text-neutral-700 cursor-pointer"
                >
                  I have read and agree to the <strong>Code of Conduct</strong>
                </label>
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="nda"
                  checked={stepData.step4?.nda || false}
                  onChange={(e) =>
                    setStepData({
                      ...stepData,
                      step4: { ...stepData.step4, nda: e.target.checked },
                    })
                  }
                  className="w-5 h-5 mt-1 text-teal-600 border-white/40 rounded focus:ring-2 focus:ring-teal-500"
                  data-testid="nda-checkbox"
                />
                <label htmlFor="nda" className="text-neutral-700 cursor-pointer">
                  I have read and agree to the{' '}
                  <strong>Non-Disclosure Agreement</strong>
                </label>
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="employment_agreement"
                  checked={stepData.step4?.employment_agreement || false}
                  onChange={(e) =>
                    setStepData({
                      ...stepData,
                      step4: {
                        ...stepData.step4,
                        employment_agreement: e.target.checked,
                      },
                    })
                  }
                  className="w-5 h-5 mt-1 text-teal-600 border-white/40 rounded focus:ring-2 focus:ring-teal-500"
                  data-testid="employment-agreement-checkbox"
                />
                <label
                  htmlFor="employment_agreement"
                  className="text-neutral-700 cursor-pointer"
                >
                  I have read and agree to the{' '}
                  <strong>Employment Agreement</strong>
                </label>
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <DocumentUploadField
              label="Profile Photo"
              docType="profile_photo"
              required={false}
            />

            <div>
              <label className="block text-sm font-medium text-navy-700 mb-2">
                Bio / About Me
              </label>
              <textarea
                rows={4}
                value={stepData.step5?.bio || ''}
                onChange={(e) =>
                  setStepData({
                    ...stepData,
                    step5: { ...stepData.step5, bio: e.target.value },
                  })
                }
                className="w-full px-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors resize-none"
                placeholder="Tell us a bit about yourself..."
                data-testid="bio-textarea"
              />
            </div>

            <div className="bg-teal-50 border border-teal-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-teal-900 mb-2">
                🎉 Almost Done!
              </h3>
              <p className="text-teal-800">
                You&apos;re about to complete your onboarding. Click Continue to
                finalize and welcome to the Half Eaten Donut team!
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white/40">
      {/* Header */}
      <header className="bg-white border-b border-white/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-xl">G</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-navy-900">girgit</h1>
                <p className="text-xs text-neutral-600">Onboarding</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Link
                to={`/applications/${applicationId}`}
                className="text-neutral-700 hover:text-navy-900 font-medium transition-colors"
              >
                Back to Application
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-neutral-700 hover:text-navy-900 transition-colors"
                data-testid="logout-button"
              >
                <LogOut className="w-5 h-5" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto py-12 px-4" data-testid="onboarding-step">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-neutral-600 mb-2">
            <span>Step {currentStep} of 5</span>
            <span>{Math.round((currentStep / 5) * 100)}% Complete</span>
          </div>
          <div className="h-2 bg-neutral-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-teal-500 transition-all duration-300"
              style={{ width: `${(currentStep / 5) * 100}%` }}
            />
          </div>
        </div>

        {/* Step Indicator */}
        <StepIndicator />

        {/* Step Content */}
        <div className="bg-white rounded-xl shadow-soft border border-white/40 p-8 mb-6">
          <div className="mb-6">
            <h2 className="text-3xl font-bold text-navy-900 mb-2">
              {ONBOARDING_STEPS[currentStep - 1].title}
            </h2>
            <p className="text-lg text-neutral-600">
              {ONBOARDING_STEPS[currentStep - 1].description}
            </p>
          </div>

          <form onSubmit={(e) => e.preventDefault()}>
            {renderStepContent()}
          </form>
        </div>

        {/* Actions */}
        <div className="flex justify-between">
          <Link
            to={`/applications/${applicationId}`}
            className="px-6 py-3 bg-white text-navy-700 border-2 border-white/40 rounded-xl hover:bg-white/60 hover:border-slate-400 transition-all duration-200 font-medium"
            data-testid="save-and-exit-button"
          >
            Save & Exit
          </Link>
          <button
            onClick={handleSubmitStep}
            disabled={submitting}
            className="px-8 py-3 bg-teal-500 text-white rounded-xl hover:bg-teal-600 transition-all duration-200 shadow-soft hover:shadow-soft font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            data-testid="continue-button"
          >
            {submitting ? (
              <>
                <Loader2 className="animate-spin mr-2 w-5 h-5" />
                Saving...
              </>
            ) : (
              <>
                {currentStep === 5 ? 'Complete Onboarding' : 'Continue'}
                <ArrowRight className="ml-2 w-5 h-5" />
              </>
            )}
          </button>
        </div>
      </main>
    </div>
  );
}
